import { bubbleMinShowMs } from './range_stairs_choreography.ts';
import { StairsBubble, StairsBubbleKind } from './range_stairs_model.ts';

// What the stairs scene's speech bubble actually shows (issue #3510, addendum 4). Pure: no React,
// no DOM, no timers - it is handed what is on screen, what the scene model now wants, and a reading
// of the scene's clock, and it answers with what to show.
//
// The machine changes its mind faster than anyone can read. A singer holding a note drops below the
// detector's confidence for a frame or two, or drifts a few cents, and the bubble would swap
// between "Hold it" and the prompt several times a second. The tone grace in the state machine
// (isHearingTone) smooths the hearing; this smooths the reading: a bubble that has gone up stands
// for bubbleMinShowMs before another may take its place, so "Try a little lower", "I'm having
// trouble hearing you" and "It's a bit noisy in here" are all on screen long enough to be read.
//
// The structural bubbles are exempt. They are the scene doing something rather than the machine
// hearing something - the hop's "Nice one!", the landing's "Great job!", the handover's
// announcement - and a picture that has already moved must not be narrated by the last line.

// The bubble on screen, and the reading of the scene clock at which it went up
export interface ShownBubble {
  bubble: StairsBubble;
  sinceMs: number;
}

const structuralKinds: readonly StairsBubbleKind[] = ['nice', 'done', 'announce'];

// Two bubbles the singer could not tell apart, which is the only equality that matters here: the
// model builds a fresh object every render, so identity says nothing.
export function sameBubble(one: StairsBubble, other: StairsBubble): boolean {
  return one.kind === other.kind && one.text === other.text && one.small === other.small;
}

export function stabilizeBubble(shown: ShownBubble | undefined, wanted: StairsBubble, nowMs: number): ShownBubble {
  if (!shown) {
    return { bubble: wanted, sinceMs: nowMs }; // nothing on screen yet: there is nothing to protect
  }
  if (sameBubble(shown.bubble, wanted)) {
    return shown;
  }
  if (shown.bubble.kind === wanted.kind) {
    // The same thing said with a line the situation has added or taken away (the hint's direction,
    // the nudge under "Can you sing this one?"). The bubble is not being replaced, so the clock is
    // not restarted: what the singer is reading stays where it is, and one more line arrives under
    // it - and a small line cannot hold the next bubble out by resetting the minimum.
    return { bubble: wanted, sinceMs: shown.sinceMs };
  }
  if (structuralKinds.includes(wanted.kind) || nowMs - shown.sinceMs >= bubbleMinShowMs) {
    return { bubble: wanted, sinceMs: nowMs };
  }
  return shown; // too soon: what is up stays up, and the wanted bubble waits its turn
}
